"use client";

import { useMemo, useState } from "react";
import { useFormContext, useWatch, type Control } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";

import {
  CodeEditor,
  type CodeEditorLanguage,
} from "@/components/Form/CodeEditor/CodeEditor";
import {
  CodeGenerateControls,
  CodeGeneratePromptBox,
} from "@/components/Form/CodeGenerateAssist";
import { NoxusFormLabel } from "@/components/Form/NoxusFormLabel";
import { Banner } from "@/components/Banner";
import { NoxusFormHelperText } from "@/components/Form/NoxusFormHelperText";
import { SpotToast } from "@/components/SpotToast";
import * as Sentry from "@sentry/nextjs";
import { InternalService } from "@/generated";
import { useSpotUser } from "@/utils/spotUser";
import { nodeOptionsToEditorInputs } from "./editorInputsFromOptions";
import { useUpstreamVariableOptions } from "./InlineVariablePicker";

/**
 * V2 renderer for `display=ConfigBindableCode`. Code editor with inline
 * `:var[...]` chips; unlike `BindableBigTextField` there is no markdown
 * round-trip — the stored value is byte-for-byte what the editor shows.
 */
export function BindableCodeField({
  nodeId,
  fieldKey,
  label,
  tooltip,
  description,
  disabled,
  placeholder,
  isRequired,
  showOptionalLabel,
  error,
  language,
  languageField,
  aiGenerate,
  numberOfLines,
}: {
  nodeId: string;
  fieldKey: string;
  label: string;
  tooltip?: string;
  description?: string;
  disabled?: boolean;
  placeholder?: string;
  isRequired?: boolean;
  showOptionalLabel?: boolean;
  error?: string;
  language?: CodeEditorLanguage;
  languageField?: string;
  aiGenerate?: "table_sql" | "table_dml" | null;
  numberOfLines?: number;
}) {
  const form = useFormContext();
  const path = `${nodeId}.${fieldKey}`;
  const options = useUpstreamVariableOptions({ inlineText: true });
  const inputs = useMemo(() => nodeOptionsToEditorInputs(options), [options]);
  const variableBindings = useWatch({
    control: form.control,
    name: `${nodeId}.variableBindings`,
  }) as Record<string, string | undefined> | undefined;
  // A sibling field (e.g. the API node's Body Type select) can drive the
  // language live; the static `language` is the fallback.
  const siblingLanguage = useWatch({
    control: form.control,
    name: `${nodeId}.${languageField ?? fieldKey}`,
    disabled: !languageField,
  }) as string | undefined;
  const effectiveLanguage: CodeEditorLanguage | undefined =
    languageField &&
    (siblingLanguage === "json" ||
      siblingLanguage === "xml" ||
      siblingLanguage === "text")
      ? siblingLanguage
      : language;
  const resolvedLabel = useMemo(
    () => label || humanize(fieldKey),
    [label, fieldKey]
  );
  const invalid = !!error;

  const { spotGroup } = useSpotUser();
  const [isTextBoxVisible, setIsTextBoxVisible] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [previousSql, setPreviousSql] = useState<string | null>(null);
  const generate = useMutation({
    mutationFn: async (prompt: string) => {
      // The endpoint derives the table schema from the group's catalog.
      const res = await InternalService.generateTableSql({
        path: { group_id: spotGroup.id },
        body: {
          prompt,
          mode: aiGenerate === "table_dml" ? "write" : "select",
        },
      });
      return res.data.sql;
    },
    onSuccess: (generatedSql) => {
      setPreviousSql(
        typeof form.getValues(path) === "string" ? form.getValues(path) : ""
      );
      form.setValue(path, generatedSql, {
        shouldDirty: true,
        shouldTouch: true,
      });
      setIsTextBoxVisible(false);
      setInputValue("");
    },
    onError: (error) => {
      SpotToast.error("Error generating code. Please try again.");
      Sentry.captureException(error, {
        extra: { user_request: inputValue },
      });
    },
  });
  const handleGenerate = () => {
    const prompt = inputValue.trim();
    if (prompt && !generate.isPending) generate.mutate(prompt);
  };
  const hasGeneratedCode = previousSql !== null;

  return (
    <div className="flex flex-col gap-1">
      <NoxusFormLabel
        label={resolvedLabel}
        tooltip={tooltip}
        name={fieldKey}
        noMargin
        isDisabled={disabled}
        isRequired={isRequired}
        showOptionalLabel={showOptionalLabel}
      />
      <CodeEditor
        control={form.control as Control<Record<string, unknown>>}
        name={path}
        placeholder={placeholder}
        isDisabled={disabled}
        inputs={inputs}
        invalid={invalid}
        nodeId={nodeId}
        variableBindings={variableBindings}
        language={effectiveLanguage}
        minRows={numberOfLines}
        dialogTitle={resolvedLabel}
        overlay={
          aiGenerate ? (
            <CodeGenerateControls
              hasGenerated={hasGeneratedCode}
              boxVisible={isTextBoxVisible}
              disabled={disabled}
              onToggleBox={() => setIsTextBoxVisible(!isTextBoxVisible)}
              onAccept={() => setPreviousSql(null)}
              onRevert={() => {
                form.setValue(path, previousSql, {
                  shouldDirty: true,
                  shouldTouch: true,
                });
                setPreviousSql(null);
              }}
            />
          ) : undefined
        }
      />
      {aiGenerate && (
        <CodeGeneratePromptBox
          visible={isTextBoxVisible}
          value={inputValue}
          onChange={setInputValue}
          onSubmit={handleGenerate}
          isLoading={generate.isPending}
          placeholder={
            aiGenerate === "table_dml"
              ? "Describe the change you want to make"
              : "Describe the query you want to generate"
          }
        />
      )}
      {description && !error ? (
        <NoxusFormHelperText>{description}</NoxusFormHelperText>
      ) : null}
      {error ? <Banner label={error} style="warning" size="sm" /> : null}
    </div>
  );
}

function humanize(key: string): string {
  return key
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}
